import { DynamicQuestionComponent } from '#app/features/form/fields/dynamicQuestionComponent.tsx'

import { BigFiveIntroduction } from '#app/features/form/forms/personalityTests/ipipNeo/bigFiveIntroduction.tsx'
import { type initialValuesIpipNeo } from '#app/features/form/forms/personalityTests/ipipNeo/initialValues.ts'
import {
	emptyZod,
	type CombinedIpipType,
} from '#app/features/form/forms/personalityTests/ipipNeo/validationSchemas.ts'
import { getCurrentStepSchema } from '#app/features/form/wizard/utils/getCurrentStepSchema.ts'
import { getAllQuestionsFromTree } from '#app/features/form/wizard/utils/getFromJSONTree.ts'

import { ClientOnly } from '#app/hooks/use-client-only.tsx'
import {
	type Steps,
	type RegisterFormHook,
	type GetSteps,
	type GetStepsParams,
} from '#types/form/wizard.ts'
import { initialQuestionsTree, ipipNeo120Tree } from './dynamicTrees.ts'
import { GoToChatOrResults } from './goToChatOrResults.tsx'

type IpipFormTypes = typeof initialValuesIpipNeo

export function GetSteps({
	currentQuestionId,
	questionTree,
	answeredQuestionsStack,
	needsController = false,
	t,
}: GetStepsParams): Steps<any>[] {
	return [
		{
			id: 'ipipNeoQuestions-empty',
			fieldKey: 'ipipNeoQuestions-empty',
			isInJsonTree: false,
			needsToBeIncludedByController: needsController,
			isController: false,
			hasBackendCall: false,
			validator: emptyZod,
			component: () => <ClientOnly>{() => <BigFiveIntroduction />}</ClientOnly>,
		},
		{
			id: 'initialQuestions',
			hasBackendCall: false,
			isController: false,
			needsToBeIncludedByController: needsController,
			isInJsonTree: true,
			fieldKey:
				`initialQuestions-${currentQuestionId}` as keyof CombinedIpipType,
			validator: getCurrentStepSchema(`initialQuestions-${currentQuestionId}`),
			questionText: getAllQuestionsFromTree(
				initialQuestionsTree(t),
				'initialQuestions',
			),
			component: ({
				register,
				getValues,
				dispatch,
				control,
				handleSubmit,
				watch,
			}: RegisterFormHook<any>) => (
				<>
					<DynamicQuestionComponent
						isController
						control={control}
						getValues={getValues}
						dispatch={dispatch}
						register={register}
						handleSubmit={handleSubmit}
						watch={watch}
						key={currentQuestionId}
						fieldKey={`initialQuestions-${currentQuestionId}`}
						questionTree={questionTree}
						answeredQuestionsStack={answeredQuestionsStack}
						currentQuestionTreeId={'initialQuestions'}
					/>
				</>
			),
		},
		{
			id: 'ipipNeoQuestions',
			hasBackendCall: false,
			isController: true,
			needsToBeIncludedByController: needsController,
			isInJsonTree: true,
			fieldKey:
				`ipipNeoQuestions-${currentQuestionId}` as keyof CombinedIpipType,
			validator: getCurrentStepSchema(`ipipNeoQuestions-${currentQuestionId}`),
			questionText: getAllQuestionsFromTree(
				ipipNeo120Tree(t),
				'ipipNeoQuestions',
			),
			component: ({
				register,
				getValues,
				dispatch,
				control,
				watch,
				handleSubmit,
			}: RegisterFormHook<IpipFormTypes>) => (
				<>
					<DynamicQuestionComponent
						isController
						control={control}
						watch={watch}
						getValues={getValues}
						dispatch={dispatch}
						register={register}
						handleSubmit={handleSubmit}
						key={currentQuestionId}
						fieldKey={`ipipNeoQuestions-${currentQuestionId}`}
						questionTree={questionTree}
						answeredQuestionsStack={answeredQuestionsStack}
						currentQuestionTreeId={'ipipNeoQuestions'}
					/>
				</>
			),
		},
		{
			id: 'redirect-to-chat-summary',
			fieldKey: 'redirect-to-chat-summary',
			isInJsonTree: false,
			needsToBeIncludedByController: needsController,
			isController: false,
			hasBackendCall: true,
			backendAction: 'redirect',
			validator: emptyZod,
			component: () => <GoToChatOrResults />,
		},
	]
}
